import React, { useEffect, useState } from "react";
import IntlMessages from "util/IntlMessages";
import { Card, Table, Row, Col, Button, Dropdown } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminTemplateList,
  deleteAdminTemplate,
  adminAddTemplate,
  adminEditTemplate,
  getAdminTemplate,
  resetTemplate,
} from "../../../appRedux/actions";
import {
  DeleteTwoTone,
  EditTwoTone,
  EllipsisOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { showConfirm, getRole } from "../../../helpers";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../components/CustomPagination";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import { useConfirmationPopup } from "../../../hooks/useConfirmationPopup";
import ComplianceTemplateDrawer from "../../../components/ComplianceTemplateDrawer";
import editIcon from "assets/images/icons/Edit.svg";
import deleteTempIcon from "assets/images/icons/delete.svg";
import LoadingBar from "../../../components/LoadingBar";

const ComplianceTemplate = (props) => {
  const navigate = useNavigate();
  const isLoaded = useSelector((state) => state.adminCompliance.isLoaded);
  const templateItem = useSelector((state) => state.adminCompliance.templateItem);
  const istemplateAdded = useSelector((state) => state.adminCompliance.istemplateAdded);
  const istemplateEdited = useSelector((state) => state.adminCompliance.istemplateEdited);
  const adminComplianceTemplateList = useSelector(
    (state) => state.adminCompliance.templateList,
  );
  const dispatch = useDispatch();
  const [data, setData] = useState([]);

  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserLegal, setIsUserLegal] = useState(false);

  // Drawer state
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirmation popup state
  const {
    isVisible: confirmationVisible,
    config: confirmationConfig,
    handleConfirm,
    handleCancel: handleConfirmationCancel,
    showDeleteConfirmation,
  } = useConfirmationPopup();

  useEffect(() => {
    setIsUserAdmin(() => {
      return getRole() === "ADMIN";
    });
    setIsUserLegal(() => {
      return getRole() === "LEGAL";
    });
    dispatch(getAdminTemplateList());
  }, []);

  useEffect(() => {
    let rowData = adminComplianceTemplateList.length
      ? adminComplianceTemplateList.map((value) => ({
        key: value.template_id,
        name: value.template_name,
        createdOn: moment(value.created_at).format("DD/MM/YYYY"),
        updatedOn: moment(value.updated_at).format("DD/MM/YYYY"),
      }))
      : [];
    setData(rowData);
  }, [isLoaded]);

  // Handle template add/edit success
  useEffect(() => {
    if (istemplateAdded || istemplateEdited) {
      setIsSubmitting(false);
      closeDrawer();
      dispatch(getAdminTemplateList());
      dispatch(resetTemplate());
    }
  }, [istemplateAdded, istemplateEdited]);

  const navigateToAddTemplate = () => {
    navigate("/admin/add-compliance-template");
  };

  // Drawer functions
  const showDrawer = () => {
    setEditMode(false);
    setEditingTemplate(null);
    setIsDrawerVisible(true);
  };

  const showEditDrawer = (item) => {
    setEditMode(true);
    setEditingTemplate(item);
    setIsDrawerVisible(true);
    // Fetch template details for editing
    dispatch(getAdminTemplate(item.key));
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setEditMode(false);
    setEditingTemplate(null);
  };

  const handleAddTemplate = (values) => {
    setIsSubmitting(true);
    dispatch(adminAddTemplate(values));
  };

  const handleEditTemplate = (values) => {
    setIsSubmitting(true);
    dispatch(adminEditTemplate(editingTemplate.key, values));
  };

  const handleSubmit = (values) => {
    if (editMode) {
      handleEditTemplate(values);
    } else {
      handleAddTemplate(values);
    }
  };

  const navigateToEditTemplate = (item) => {
    showEditDrawer(item);
  };

  const showDeleteTemplate = (item) => {
    showDeleteConfirmation(
      item.name,
      () => {
        dispatch(
          deleteAdminTemplate(item.key, () => {
            dispatch(getAdminTemplateList());
          }),
        );
      }
    );
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
    },
    {
      title: "Updated On",
      dataIndex: "updatedOn",
      key: "updatedOn",
    },
    {
      title: "Action",
      key: "operation",
      align: "center",
      render: (item) => {
        const menuItems = [
          {
            key: 'title',
            label: 'Actions',
            className: 'actionDropdownTitle'
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: <img src={editIcon} alt="Edit" width={16} height={16} />,
            disabled: !(isUserAdmin || isUserLegal),
            onClick: () => navigateToEditTemplate(item)
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <img src={deleteTempIcon} alt="Delete" width={16} height={16} />,
            disabled: !(isUserAdmin || isUserLegal),
            onClick: () => showDeleteTemplate(item)
          }
        ];

        return (
          <Dropdown
            menu={{
              items: menuItems,
              style: { padding: "10px 5px" },
              className: "actionListdropdown"
            }}
            trigger={["click"]}
          >
            <Button
              type="link"
              icon={<EllipsisOutlined style={{ fontSize: "20px" }} />}
            />
          </Dropdown>
        );
      },
    }
  ];


  return (
    <div>
      <Row>
        <Col span={24}>
          <div className="gx-flex-row align-items-center">
            <h1 className="title gx-mb-4">
              <IntlMessages id="sidebar.compliance-template" />
            </h1>
            <Button
              id="add-template-btn"
              type="primary"
              className="gx-ml-auto"
              onClick={showDrawer}
              disabled={isUserAdmin || isUserLegal ? false : true}
            >
              <MedicineBoxOutlined /> Add Template
            </Button>
          </div>
        </Col>
        <Col span={24}>
          <Card className="mb-0">
            <Table
              className="gx-table-responsive mpp-list-table"
              columns={columns}
              dataSource={data}
              bordered
              pagination={false}
            />

          </Card>
        </Col>
      </Row>

      {/* Confirmation Popup */}
      <ConfirmationPopup
        visible={confirmationVisible}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        confirmText={confirmationConfig.confirmText}
        cancelText={confirmationConfig.cancelText}
        confirmButtonType={confirmationConfig.confirmButtonType}
        onConfirm={handleConfirm}
        onCancel={handleConfirmationCancel}
      />

      {/* Compliance Template Drawer */}
      {isSubmitting ? (
        <LoadingBar loading={isSubmitting} />
      ) : (
        <ComplianceTemplateDrawer
          visible={isDrawerVisible}
          onClose={closeDrawer}
          onSubmit={handleSubmit}
          // isLoading={isSubmitting}
          editMode={editMode}
          initialValues={editMode && templateItem ? {
            template_name: templateItem.template_name,
            template: templateItem.template
          } : null}
        />
      )}
    </div>
  );
};

export default ComplianceTemplate;